/**
 * DurableJobStatus - the honest stage machine for one durable ingestion job
 * (Story #5351 W1-T10, Screen Contract G04: "Keep durable work discoverable
 * across navigation, reload, operator change and worker restart, with
 * stage, child outcomes and provider reconciliation kept honest.").
 *
 * Pure presentational: it takes the read result (job/isLoading/isError) as
 * props and derives its display purely from real IngestionJob fields — it
 * never invents a state the backend has not reported. That is also why this
 * file makes zero API calls: JobsDrawer owns the fetch, this owns rendering
 * it truthfully, so it can be unit-tested per stage with no network/query
 * plumbing at all.
 *
 * Stage derivation (deliberately conservative):
 *  - PENDING/SCHEDULED/DRAFT -> 'accepted'  (submitted, not yet running)
 *  - RUNNING                 -> 'running'
 *  - PAUSED/ON_HOLD          -> 'paused'
 *  - COMPLETED               -> 'succeeded', UNLESS the job's own latest run
 *                                record reports records_failed > 0, in which
 *                                case it is 'partial' — a real signal from
 *                                run_history, never a guess.
 *  - FAILED/CANCELLED        -> 'failed'
 *  - anything else (a future backend status this build does not know about)
 *                             -> 'unknown' ("Outcome unknown" per the G04
 *                                mockup — never silently rendered as any of
 *                                the above).
 *
 * 'accepted'/'running' additionally carry a stalled flag when updated_at is
 * older than STALE_AFTER_MS — an honest staleness signal derived from the
 * job's own timestamp, not a fabricated timeout state.
 */
import React from 'react';
import type { IngestionJob } from '../../services/api';
import StatePanel from '../ui/StatePanel';
import UnavailableReason from '../ui/UnavailableReason';

export type JobStageCategory = 'accepted' | 'running' | 'paused' | 'succeeded' | 'partial' | 'failed' | 'unknown';

export interface DerivedJobStage {
  category: JobStageCategory;
  label: string;
  stalled: boolean;
}

/** A running/accepted job with no update in this long is flagged stalled — an honest staleness read, not a guess at cause. */
const STALE_AFTER_MS = 10 * 60 * 1000;

function latestRunFailedCount(job: IngestionJob): number | undefined {
  const history = job.run_history;
  if (!history || history.length === 0) return undefined;
  return history[history.length - 1]?.records_failed;
}

export function deriveJobStage(job: IngestionJob, now: number = Date.now()): DerivedJobStage {
  const updatedAt = job.updated_at ? new Date(job.updated_at).getTime() : NaN;
  const isStale = Number.isFinite(updatedAt) && now - updatedAt > STALE_AFTER_MS;

  switch (job.status) {
    case 'DRAFT':
      return { category: 'accepted', label: 'Draft', stalled: false };
    case 'SCHEDULED':
      return { category: 'accepted', label: 'Scheduled', stalled: false };
    case 'PENDING':
      return { category: 'accepted', label: 'Accepted', stalled: isStale };
    case 'RUNNING':
      return { category: 'running', label: 'Running', stalled: isStale };
    case 'ON_HOLD':
      return { category: 'paused', label: 'On hold', stalled: false };
    case 'PAUSED':
      return { category: 'paused', label: 'Paused', stalled: false };
    case 'COMPLETED': {
      const failed = latestRunFailedCount(job);
      if (failed && failed > 0) {
        return { category: 'partial', label: `Partial — ${failed} record(s) failed`, stalled: false };
      }
      return { category: 'succeeded', label: 'Completed', stalled: false };
    }
    case 'FAILED':
      return { category: 'failed', label: 'Failed', stalled: false };
    case 'CANCELLED':
      return { category: 'failed', label: 'Cancelled', stalled: false };
    default:
      // A status value this build does not recognise. Never rendered as any
      // known-good state (DoD-07) — this is the mockup's "Outcome unknown".
      return { category: 'unknown', label: 'Outcome unknown', stalled: false };
  }
}

const CATEGORY_BADGE: Record<JobStageCategory, string> = {
  accepted: 'bg-slate-100 text-slate-700',
  running: 'bg-sky-100 text-sky-700',
  paused: 'bg-amber-100 text-amber-700',
  succeeded: 'bg-emerald-100 text-emerald-700',
  partial: 'bg-amber-100 text-amber-800',
  failed: 'bg-red-100 text-red-700',
  unknown: 'bg-slate-200 text-slate-700',
};

export interface DurableJobStatusProps {
  jobId: string;
  job?: IngestionJob;
  isLoading: boolean;
  isError: boolean;
}

const DurableJobStatus: React.FC<DurableJobStatusProps> = ({ jobId, job, isLoading, isError }) => {
  if (isLoading) {
    return <StatePanel state="loading" message={`Loading job ${jobId}…`} />;
  }
  if (isError) {
    return (
      <StatePanel
        state="unreachable"
        reason={`Could not reach the ingestion jobs source for ${jobId}.`}
      />
    );
  }
  if (!job) {
    return (
      <StatePanel
        state="unknown"
        message={`No record found for job ${jobId}.`}
        reason="The job may not exist, or the read source has not indexed it yet — this is not evidence it failed."
      />
    );
  }

  const stage = deriveJobStage(job);

  return (
    <div data-testid="durable-job-status" data-stage-category={stage.category} className="space-y-3">
      <div className="flex items-center gap-2">
        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold ${CATEGORY_BADGE[stage.category]}`}>
          {stage.label}
        </span>
        <span className="text-sm text-gray-500 font-mono">{job.job_id}</span>
      </div>

      <dl className="grid grid-cols-2 gap-x-4 gap-y-1.5 text-sm">
        <dt className="text-gray-500">Target</dt>
        <dd className="text-gray-900">{job.target_database}</dd>
        <dt className="text-gray-500">Mode</dt>
        <dd className="text-gray-900">{job.mode}</dd>
        <dt className="text-gray-500">Progress</dt>
        <dd className="text-gray-900">{job.progress_pct}%</dd>
        <dt className="text-gray-500">Updated</dt>
        <dd className="text-gray-900" title={job.updated_at}>{job.updated_at}</dd>
      </dl>

      {stage.stalled && (
        <UnavailableReason reason={`No update from the job source in over ${STALE_AFTER_MS / 60000} minutes — this may be stalled.`} />
      )}

      {stage.category === 'unknown' && (
        <UnavailableReason
          reason={`Status "${job.status}" is not recognised by this build. Do not retry — reconcile the job in Operations first.`}
        />
      )}

      {stage.category === 'partial' && (
        <UnavailableReason reason="Some records in the latest run failed. Aggregate success is not shown — open the job for child outcomes." />
      )}
    </div>
  );
};

export default DurableJobStatus;
